<script setup>
import { computed, onMounted, watch } from 'vue'

import CookieBanner from '@/components/shared/ui/CookieBanner.vue'
import DebugBanner from '@/components/shared/ui/DebugBanner.vue'
import MaintenanceBanner from '@/components/shared/ui/MaintenanceBanner.vue'
import TalkativeWidget from '@/components/shared/widget/TalkativeWidget.vue'

import { useTabHandler } from '@/composables/tabHandler'
import { useAuthStore } from '@/pinia/auth'
import { useBrandedStore } from '@/pinia/branded'
import { useCustomerStore } from '@/pinia/customer'
import { useStyleStore } from '@/pinia/style'
import { useRoute } from 'vue-router'
import { authGetCompanyWebSettings } from './api'
import { config } from './utils/config'

const styleStore = useStyleStore()
const customerStore = useCustomerStore()
const authStore = useAuthStore()
const brandedStore = useBrandedStore()
const route = useRoute()

useTabHandler()

onMounted(async () => {
  await initialiseStoreData()
  authStore.login_url = ['staging', 'prod'].includes(config.environment.toLowerCase()) ? '/login' : '/login-admin'
})

const hasTalkative = computed(() => {
  return authStore.isPortalUser && styleStore.fabricator?.name === 'endurance'
})

const showDebugBanner = computed(() => {
  return !config.environment.toLowerCase().includes('prod')
})

const brandedRoute = computed(() => {
  return route.meta.app === 'branded' || route.meta.brandedLaunch
})

const documentTitle = computed(() => {
  const pageName = styleStore.pageName || ''
  const siteTitle = brandedRoute.value
    ? brandedStore.brandedSettings?.companyName || 'QUOTILA'
    : styleStore.fabricator?.siteTitle || 'QUOTILA'

  const titleParts = [siteTitle, pageName]

  if (!brandedRoute.value) {
    const installation = window.VITE_APP_INSTALLATION_TYPE || ''
    titleParts.push(installation.charAt(0).toUpperCase() + installation.slice(1))
  }

  return titleParts.filter(Boolean).join(' - ')
})

watch(
  () => authStore.usingBrandedModeToken,
  async (usingBrandedModeToken) => {
    if (usingBrandedModeToken) {
      const response = await authGetCompanyWebSettings()
      const tagSetting = response?.companyWebSettings.find((x) => x.key === 'tagId')
      if (tagSetting) {
        window.gtag('config', 'G-' + tagSetting.value)
      }
    }
  },
  { immediate: true }
)

watch(
  () => [styleStore.fabricator_logo, customerStore.customer_logo, styleStore.customer_logo],
  async () => {
    var existingFavicons = document.querySelectorAll("link[rel*='icon']")
    existingFavicons.forEach(function (link) {
      link.remove()
    })
    var link = document.createElement('link')
    link.type = 'image/png'
    link.rel = 'icon'
    if (authStore.isPortalUser && authStore.usingBrandedModeToken) {
      link.href = customerStore.customer_logo || styleStore.customer_logo || '/favicon.ico'
    } else {
      link.href = styleStore.fabricator_logo || '/favicon.ico'
    }
    document.head.appendChild(link)
  },
  { immediate: true }
)

async function initialiseStoreData() {
  await styleStore.refresh()
}

watch(
  documentTitle,
  (title) => {
    window.document.title = title
  },
  { immediate: true }
)
</script>

<template>
  <div id="app">
    <MaintenanceBanner />
    <DebugBanner v-if="showDebugBanner" />
    <router-view />
    <TalkativeWidget v-if="hasTalkative" />
    <CookieBanner />
  </div>
</template>

<style src="./assets/css/styles.css"></style>
